import { Activity, ActivityType, ActivityWithDetails, AdminActivitiesResponse } from '@fsp/types';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { apiClient } from '../services/apiClient';
import { formatDate, DateFormatOptions } from '@fsp/shared';
import OrganisationChip from './OrganisationChip';
import UserChip from './UserChip';

interface ActivityListProps {
  endpoint: string;
  showOrganisations?: boolean;
  showUsers?: boolean;
}

// Subtle color scheme that matches the theme
const getActivityTypeStyle = (activityType: ActivityType) => {
  switch (activityType) {
    case ActivityType.AUTH:
      return {
        backgroundColor: '#dbeafe', // Light blue
        color: '#1e40af', // Dark blue
        borderColor: '#93c5fd',
      };
    case ActivityType.USER:
      return {
        backgroundColor: '#ede9fe', // Light purple
        color: '#5b21b6', // Dark purple
        borderColor: '#c4b5fd',
      };
    case ActivityType.ORGANISATION:
      return {
        backgroundColor: '#f3f4f6', // Light gray
        color: '#374151', // Dark gray
        borderColor: '#d1d5db',
      };
    case ActivityType.TEAM_MEMBER:
      return {
        backgroundColor: '#e0e7ff', // Light indigo
        color: '#3730a3', // Dark indigo
        borderColor: '#a5b4fc',
      };
    case ActivityType.SUBSCRIPTION:
      return {
        backgroundColor: '#d1fae5', // Light green
        color: '#065f46', // Dark green
        borderColor: '#6ee7b7',
      };
    case ActivityType.PLAN:
      return {
        backgroundColor: '#fce7f3', // Light pink
        color: '#831843', // Dark pink
        borderColor: '#f9a8d4',
      };

    case ActivityType.RECORD:
      return {
        backgroundColor: '#e0e7ff', // Light indigo
        color: '#3730a3', // Dark indigo
        borderColor: '#a5b4fc',
      };
    case ActivityType.AUDIT:
      return {
        backgroundColor: '#f3e8ff', // Light purple
        color: '#6b21a8', // Dark purple
        borderColor: '#d8b4fe',
      };
    case ActivityType.INVITATION:
      return {
        backgroundColor: '#e0f2fe', // Light cyan
        color: '#164e63', // Dark cyan
        borderColor: '#7dd3fc',
      };
    case ActivityType.OTHER:
    default:
      return {
        backgroundColor: '#f9fafb', // Very light gray
        color: '#6b7280', // Medium gray
        borderColor: '#e5e7eb',
      };
  }
};

export default function ActivityList({
  endpoint,
  showOrganisations = false,
  showUsers = true,
}: ActivityListProps) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterType, setFilterType] = useState<ActivityType | 'ALL'>('ALL');

  // State for fetched data
  const [activities, setActivities] = useState<Activity[]>([]);
  const [users, setUsers] = useState<
    Array<{
      id: string;
      email: string;
      firstName: string | null;
      lastName: string | null;
      avatarUrl: string | null;
    } | null>
  >([]);
  const [organisations, setOrganisations] = useState<Array<{ id: string; name: string } | null>>(
    []
  );
  const [plans, setPlans] = useState<Array<{ id: string; name: string } | null>>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [availableTypes, setAvailableTypes] = useState<ActivityType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch data from endpoint with pagination and filtering
  useEffect(() => {
    const fetchActivities = async () => {
      setLoading(true);
      setError(null);
      try {
        // Build URL with pagination and filter parameters
        const url = new URL(endpoint, window.location.origin);
        const offset = page * rowsPerPage;

        // Add or update limit and offset params
        url.searchParams.set('limit', rowsPerPage.toString());
        url.searchParams.set('offset', offset.toString());

        // Add filter if not "ALL"
        if (filterType !== 'ALL') {
          url.searchParams.set('activityType', filterType);
        }

        const response = await apiClient.get(url.pathname + url.search);

        if (!response.ok) {
          throw new Error('Failed to fetch activities');
        }

        const data: AdminActivitiesResponse = await response.json();

        // Extract activities, users, and organisations into separate arrays
        setActivities(data.activities.map((item: ActivityWithDetails) => item.activity));
        setUsers(data.activities.map((item: ActivityWithDetails) => item.user));
        setOrganisations(data.activities.map((item: ActivityWithDetails) => item.organisation));
        setPlans(data.activities.map((item: ActivityWithDetails) => item.plan));
        setTotalCount(data.total);

        // Extract available types if we have data
        if (data.activities.length > 0 && filterType === 'ALL') {
          const types = new Set(
            data.activities.map((item: ActivityWithDetails) => item.activity.activityType)
          );
          setAvailableTypes(Array.from(types).sort());
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        console.error('Error fetching activities:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [endpoint, page, rowsPerPage, filterType]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 25));
    setPage(0);
  };

  const handleFilterChange = (newFilter: ActivityType | 'ALL') => {
    setFilterType(newFilter);
    setPage(0); // Reset to first page when filter changes
  };

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <>
      {/* Filter Control */}
      {availableTypes.length > 0 && (
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="flex-end"
          sx={{ mb: 2 }}
        >
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="activity-type-filter-label">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <FilterListIcon fontSize="small" />
                <span>Filter by Type</span>
              </Stack>
            </InputLabel>
            <Select
              labelId="activity-type-filter-label"
              value={filterType}
              onChange={(e) => handleFilterChange(e.target.value as ActivityType | 'ALL')}
              label="Filter by Type"
            >
              <MenuItem value="ALL">
                <Typography variant="body2">All Types</Typography>
              </MenuItem>
              {availableTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  <Chip
                    label={type}
                    size="small"
                    sx={{
                      ...getActivityTypeStyle(type),
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      height: 22,
                    }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="120">Type</TableCell>
                <TableCell sx={{ minWidth: 300 }}>Description</TableCell>
                {showUsers && <TableCell width="200">User</TableCell>}
                {showOrganisations && <TableCell width="200">Organisation</TableCell>}
                <TableCell width="180">Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activities.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={3 + (showUsers ? 1 : 0) + (showOrganisations ? 1 : 0)}
                    align="center"
                    sx={{ py: 4 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No activities found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                activities.map((activity, index) => {
                  const user = users[index];
                  const organisation = organisations[index];
                  return (
                    <TableRow key={activity.id}>
                      <TableCell>
                        <Chip
                          label={activity.activityType}
                          size="small"
                          sx={{
                            ...getActivityTypeStyle(activity.activityType),
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 20,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 300 }}>
                        <Typography variant="body2">{activity.description}</Typography>
                      </TableCell>
                      {showUsers && (
                        <TableCell>
                          <UserChip user={user} />
                        </TableCell>
                      )}
                      {showOrganisations && (
                        <TableCell>
                          <OrganisationChip organisation={organisation} />
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(activity.createdAt, DateFormatOptions.DATEANDTIME)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {!loading && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
        />
      )}
    </>
  );
}
